import { notification, Segmented } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AnswerTypeEnum,
  PlanFeaturesIds,
  TestFileType,
  StrategyValues,
} from '../../constants/enums';
import useCreateScreening from '../../hooks/useCreateScreening';
import useCreateTestGroup from '../../hooks/useCreateTestGroup';
import { t } from '../../i18n/i18n';
import { TrackEvent } from '../../utils/filters/segmentUtils';
import DisabledFeature from '../DisabledFeature/DisabledFeature';
import DropDownSelect from '../DropdownSelect/DropdownSelect';
import TreeMultiSelect from '../TreeMultiSelect/TreeMultiSelect';
import './AddQuestion.scss';
import { getSubSkills } from '../../API/services/screeningService';
import AnswerTypeButton from './AnswerTypeButton/AnswerTypeButton';
import Ai from '../../assets/additionalQuestions/ai';
import ModalQuestionAI from '../ModalQuestionAI/ModalQuestionAI';
import { getQuestionTypeButton } from '../../utils/getQuestionTypeButtons';
import {
  AIDifficultyId,
  bloomTaxonomyOptions,
  durationOptions,
  questionTypeOptions,
  textAnswerDifficultyId,
  videoAnswerDifficultyId,
  weightOptions,
} from '../../constants/questions';
import QuestionComponentSelector from '../QuestionComponentSelected/QuestionComponentSelected';
import { determineDisabledMinimumTier } from '../../utils/determineDisabled';

const AddQuestions = ({ testGroupABM, setShowDropdown, readOnly = false }) => {
  const { testDifficulty, testgroupWithThemes, subSkillsOptions } = useCreateTestGroup();
  const {
    currentTypeQuestion,
    isEdit,
    currentQuestionData,
    disableAddCheckQuestion,
    disableAddFileQuestion,
    disableAddTextQuestion,
    disableAddTextIaQuestion,
    incompleteQuestion,
    setTestAnswer,
    handleEditQuestion,
    handleAddQuestion,
    handleAddQuestionDifficulty,
    handleAddQuestionText,
    eachScore,
    disableAddCodeQuestion,
    disableAddWriteQuestion,
    disableAddChallengeAsyncQuestion,
    handleAddQuestionWeight,
    promptsCorrectionTextAI,
    fetchPromptCorrectionTextAI,
    handleAddQuestionExplanation,
    handleAddQuestionSubskills,
    customTestGroupStrategy,
    newScreening,
    handleCustomTestGroupStrategy,
    handleCurrentTypeQuestion,
    handleAddQuestionDuration,
    handleAddQuestionType,
    handleAddBloomTaxonomy,
  } = useCreateScreening();
  const { pathname } = useLocation();

  const track = pathname === '/createscreening';
  const disableEachScore =
    currentTypeQuestion === AnswerTypeEnum.CHECK &&
    (!currentQuestionData?.options?.length >= 2 ||
      !currentQuestionData?.options?.every((answer) => answer?.text.length));

  const buttons = useMemo(
    () => getQuestionTypeButton(testGroupABM, true, customTestGroupStrategy),
    [customTestGroupStrategy, testGroupABM]
  );

  useEffect(() => {
    if (promptsCorrectionTextAI?.length === 0 || !promptsCorrectionTextAI) {
      fetchPromptCorrectionTextAI(testGroupABM);
    }
  }, []);

  const hasAllTimesForWebcam = Boolean(
    currentQuestionData?.preparationTime &&
      currentQuestionData?.totalTime &&
      currentQuestionData?.maxAttempts
  );

  const disableQuestionButton = Boolean(
    (!currentQuestionData?.text?.length &&
      currentTypeQuestion !== AnswerTypeEnum.CHALLENGE_ASYNC_AI) ||
      disableAddTextQuestion ||
      disableAddTextIaQuestion ||
      (![AnswerTypeEnum.CODE, AnswerTypeEnum.CODE_AI].includes(currentTypeQuestion) &&
        currentQuestionData?.options?.some((option) => !option.text?.trim()?.length)) ||
      disableAddFileQuestion ||
      (eachScore ? disableEachScore : disableAddCheckQuestion) ||
      (AnswerTypeEnum.VIDEO === currentTypeQuestion && !hasAllTimesForWebcam) ||
      (AnswerTypeEnum.DRAW === currentTypeQuestion &&
        (!currentQuestionData?.options?.[0]?.files?.length || !hasAllTimesForWebcam)) ||
      disableAddWriteQuestion ||
      disableAddChallengeAsyncQuestion
  );

  const disabledTestGroupABMButton =
    readOnly ||
    disableQuestionButton ||
    disableAddCodeQuestion ||
    (AnswerTypeEnum.VIDEO !== currentTypeQuestion &&
      AnswerTypeEnum.TEXT !== currentTypeQuestion &&
      !currentQuestionData?.difficultyId) ||
    (testgroupWithThemes && !currentQuestionData?.theme) ||
    (AnswerTypeEnum.DRAW === currentTypeQuestion &&
      (!currentQuestionData?.options?.[0]?.files?.length || !hasAllTimesForWebcam)) ||
    ([AnswerTypeEnum.CHECK, AnswerTypeEnum.RADIO].includes(currentTypeQuestion) &&
      !currentQuestionData?.options?.some((option) => option.correct === true) &&
      !eachScore) ||
    currentQuestionData?.testFiles?.some(
      (file) =>
        file.fileType === TestFileType.AUDIO && !file?.infinitePlays && !Number(file?.numberOfPlays)
    );

  const handleCustomQuestion = (callback) => {
    if (testGroupABM && eachScore) {
      if (
        currentQuestionData.options.some((option) => option.weight === '') ||
        currentQuestionData.options.filter(
          (option) =>
            (testGroupABM && Number(option.weight) > currentQuestionData?.weight) ||
            (testGroupABM && Number(option.weight) < -currentQuestionData?.weight)
        ).length
      ) {
        notification.open({
          message: testGroupABM
            ? t('CREATE_TEST_GROUP_MESSAGE_ERROR').replaceAll(
                '{score}',
                currentQuestionData?.weight
              )
            : t('CREATE_TEST_INVALID_CUSTOM_WEIGHT'),
          type: 'error',
        });
        return;
      }
      if (
        !currentQuestionData.options.some(
          (option) => Number(option.weight) === currentQuestionData?.weight
        )
      ) {
        notification.open({
          message: t('CREATE_TEST_GROUP_INVALID_OPTION'),
          type: 'error',
        });
        return;
      }
      callback(testGroupABM);
      return;
    }
    setTestAnswer({
      text: '',
      score: undefined,
      explanation: undefined,
    });

    callback(testGroupABM);
  };

  useEffect(() => {
    if (testGroupABM && currentTypeQuestion === AnswerTypeEnum.VIDEO) {
      const videoDifficulty = testDifficulty?.find((test) => test?.id === videoAnswerDifficultyId);
      handleAddQuestionDifficulty({
        id: videoDifficulty?.id,
        name: t(`CREATE_TEST_GROUP_QUESTION_${videoDifficulty?.name}`),
        weight: videoDifficulty?.score,
      });
    }

    if (testGroupABM && currentTypeQuestion === AnswerTypeEnum.TEXT) {
      const textDifficulty = testDifficulty?.find((test) => test?.id === textAnswerDifficultyId);
      handleAddQuestionDifficulty({
        id: textDifficulty?.id,
        name: t(`CREATE_TEST_GROUP_QUESTION_${textDifficulty?.name}`),
        weight: textDifficulty?.score,
      });
    }
  }, [currentTypeQuestion]);

  useEffect(() => {
    // when strategy on change, select first available question type
    const questionTypes = getQuestionTypeButton(false, true, customTestGroupStrategy)?.filter(
      (x) => x.show
    );
    handleCurrentTypeQuestion(questionTypes?.[0]?.id);
  }, [customTestGroupStrategy]);

  const strategyOptions = [
    {
      label: <span>{t('TESTGROUP_STRATEGY_NORMAL_LABEL')}</span>,
      value: StrategyValues.NORMAL,
    },
    {
      label: (
        <span role="img" aria-label="AI">
          {t('TESTGROUP_STRATEGY_AI_INTERVIEW_LABEL')}
        </span>
      ),
      value: StrategyValues.AI_INTERVIEW,
      disabled: determineDisabledMinimumTier(1),
    },
  ];

  return (
    <div
      className={`w-100 row justify-content-center my-3 d-flex flex-column align-items-center ${
        incompleteQuestion && 'border-danger'
      }`}
    >
      {testGroupABM && (
        <div className="w-100 d-flex flex-column align-items-center gap-2 pb-2">
          <div className="d-flex align-items-center w-100">
            <input
              placeholder={t('ABM_NAME_INPUT_PLACEHOLDER')}
              value={currentQuestionData?.name ?? ''}
              onChange={handleAddQuestionText}
              name="name"
              disabled={readOnly}
              className="mx-2 rounded py-2 text-xs font-weight-bold theme-input-border outline-none  py-1 px-2 outline-none h-10"
            />
            <DropDownSelect
              selectedPlaceholder
              className="ml-0 mr-2"
              placeholder={t('MULTIPLE_SELECT_DIFFICULTY_PLACEHOLDER')}
              setState={(e) => handleAddQuestionDifficulty(e)}
              selected={currentQuestionData?.difficultyId}
              options={testDifficulty
                ?.filter((test) =>
                  [AnswerTypeEnum.CHECK, AnswerTypeEnum.RADIO].includes(currentTypeQuestion)
                    ? test.id !== AIDifficultyId && test.id !== videoAnswerDifficultyId
                    : true
                )
                ?.sort((a, b) => a.order - b.order)
                ?.map((item) => ({
                  id: item.id,
                  name: t(`CREATE_TEST_GROUP_QUESTION_${item.name}`),
                  weight: item.score,
                }))}
              disabled={
                [AnswerTypeEnum.TEXT, AnswerTypeEnum.VIDEO].includes(currentTypeQuestion) ||
                readOnly
              }
            />
            {testgroupWithThemes && (
              <input
                placeholder={t('ABM_THEME_INPUT_PLACEHOLDER')}
                value={currentQuestionData?.theme ?? ''}
                onChange={handleAddQuestionText}
                disabled={readOnly}
                name="theme"
                className="mx-2 rounded py-2 text-xs font-weight-bold theme-input-border outline-none  py-1 px-2 outline-none h-25"
                data-testid="add-questions-abm-theme-input"
              />
            )}
            <div className="d-flex align-items-center gap-1">
              {Boolean(currentQuestionData?.weight) && (
                <>
                  <span>{t('CREATE_TEST_GROUP_SCORE')}</span>
                  <DropDownSelect
                    selectedPlaceholder
                    setState={(e) => handleAddQuestionWeight(e)}
                    selected={currentQuestionData?.weight}
                    options={weightOptions}
                    disabled={[AnswerTypeEnum.TEXT, AnswerTypeEnum.VIDEO].includes(
                      currentTypeQuestion
                    )}
                  />
                </>
              )}
              {eachScore && currentQuestionData?.difficultyId && (
                <p
                  className=" text-xs fit-content mb-0 font-weight-500 px-2"
                  data-testid="add-questions-each-score-p"
                >
                  {t('CREATE_TEST_GROUP_TEXT_BETWEEN_SCORE').replaceAll(
                    '{score}',
                    currentQuestionData?.weight
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="d-flex w-100 px-2">
            <textarea
              name="text"
              className={`border-grey-2 outline-none w-100  p-2 px-3 text-xs ${
                currentQuestionData?.explanation?.length ? 'selected-border' : 'unselected-border'
              }`}
              rows="3"
              placeholder={t('CREATE_SCREENING_EXPLANATION_PLACEHOLDER')}
              onChange={handleAddQuestionExplanation}
              value={currentQuestionData?.explanation ?? ''}
              disabled={readOnly}
              maxLength={300}
              data-testid="question-text-explanation"
            />
          </div>
          <div className="d-flex w-100 px-2 gap-1 align-items-center">
            <TreeMultiSelect
              key={currentQuestionData?.subSkillIds?.join(',') || 'empty'}
              options={subSkillsOptions}
              value={currentQuestionData?.subSkillIds || []}
              onChange={(values) => {
                handleAddQuestionSubskills(values);
              }}
              label={t('MULTIPLE_SELECT_SUBSKILLS_PLACEHOLDER')}
              className="w-100"
              treeCheckStrictly={false}
              dropdownWidth="400px"
              disabled={readOnly}
            />
            <DropDownSelect
              selectedPlaceholder
              placeholder={t('MULTIPLE_SELECT_DURATION_PLACEHOLDER')}
              setState={(e) => handleAddQuestionDuration(e)}
              selected={currentQuestionData?.duration}
              options={durationOptions}
              className="w-100"
              dropdownWidth="400px"
            />
            <DropDownSelect
              selectedPlaceholder
              placeholder={t('MULTIPLE_SELECT_QUESTION_TYPE_PLACEHOLDER')}
              setState={(e) => handleAddQuestionType(e)}
              selected={currentQuestionData?.questionType}
              options={questionTypeOptions}
              className="w-100"
              dropdownWidth="400px"
            />
            <DropDownSelect
              selectedPlaceholder
              placeholder={t('MULTIPLE_SELECT_BLOOM_TAXONOMY_PLACEHOLDER')}
              setState={(e) => handleAddBloomTaxonomy(e)}
              selected={currentQuestionData?.bloomTaxonomy}
              options={bloomTaxonomyOptions}
              className="w-100"
              dropdownWidth="400px"
            />
          </div>
        </div>
      )}

      <div className="d-flex flex-column w-100 ">
        <div className="my-4 mx-2">
          <Segmented
            disabled={newScreening?.customTestGroup?.length > 0}
            options={strategyOptions}
            value={customTestGroupStrategy}
            onChange={handleCustomTestGroupStrategy}
          />
        </div>
        <div className="d-flex w-100 h-100 px-2">
          <div className="w-25 column justify-content-center column align-items-end">
            <div className="empty-buttons" />
            {buttons.map((button, index) =>
              button.show ? (
                button.isPremiumFeature ? (
                  <DisabledFeature
                    className="position-relative flex-1 w-auto py-0 d-flex flex-column align-items-end "
                    childrenClassname="position-relative w-100"
                    iconClassName="disabled-icon-add-questions"
                    featureId={PlanFeaturesIds.VIDEO_ANSWERS}
                    validateAmount={false}
                    usedAmount={0}
                  >
                    <AnswerTypeButton button={button} index={index} disabled={readOnly} />
                  </DisabledFeature>
                ) : (
                  <AnswerTypeButton button={button} index={index} disabled={readOnly} />
                )
              ) : null
            )}
            <div className="empty-buttons" />
          </div>

          <div className="w-100 ml-2 pt-4 question-to-add-container d-flex flex-column justify-content-between">
            <QuestionComponentSelector
              currentTypeQuestion={currentTypeQuestion}
              testGroupABM={testGroupABM}
              readOnly={readOnly}
            />
            {!readOnly && (
              <div className="d-flex justify-content-end">
                {isEdit ? (
                  <button
                    disabled={testGroupABM ? disabledTestGroupABMButton : disableQuestionButton}
                    className={`mt-4 align-self-end px-3 py-1 rounded-pill  text-white text-xs font-weight-bold border-0 outline-none ${
                      (testGroupABM ? disabledTestGroupABMButton : disableQuestionButton)
                        ? 'disabled-button'
                        : 'bg-blue-principal'
                    }`}
                    onClick={() => {
                      handleCustomQuestion(handleEditQuestion);
                    }}
                    type="button"
                    data-testid="add-questions-edit-question-btn"
                  >
                    {t('CREATE_SCREENING_YOUR_QUESTIONS_EDIT_QUESTION')}
                  </button>
                ) : (
                  <button
                    disabled={testGroupABM ? disabledTestGroupABMButton : disableQuestionButton}
                    className={`mt-4 align-self-end  justify-content-end px-3 py-1 rounded-0  text-white text-xs font-weight-bold border-0 outline-none ${
                      (testGroupABM ? disabledTestGroupABMButton : disableQuestionButton)
                        ? 'disabled-button'
                        : 'bg-blue-principal'
                    }`}
                    onClick={() => {
                      if (track) {
                        TrackEvent(
                          `create-screening-add-${currentTypeQuestion.toLowerCase()}-question`
                        );
                      }
                      handleCustomQuestion(handleAddQuestion);
                      if (
                        !testGroupABM &&
                        currentTypeQuestion !== AnswerTypeEnum.CHALLENGE_ASYNC_AI
                      )
                        setShowDropdown(true);
                    }}
                    type="button"
                    data-testid="add-questions-create-question-btn"
                  >
                    {currentTypeQuestion === AnswerTypeEnum.CHALLENGE_ASYNC_AI &&
                    newScreening?.customChallengeTestGroup?.length > 0
                      ? t('CREATE_SCREENING_YOUR_QUESTIONS_EDIT_QUESTION')
                      : t('CREATE_SCREENING_YOUR_QUESTIONS_SAVE_QUESTION')}
                  </button>
                )}
              </div>
            )}
            <ModalQuestionAI
              answersType={currentTypeQuestion}
              testGroupABM={testGroupABM}
              aiImg={<Ai />}
              handleCustomQuestion={handleCustomQuestion}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuestions;
